import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import * as Icon from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../core/data/redux/store';
import {
  createProject,
  updateProject,
  getAllProjects,
} from '../../../../services/projectServices';
import { getAllLeads } from '../../../../services/leadServices';

import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';

const ProjectsModal = ({
  selectedProject,
  setIsAddModalOpen,
  isAddModalOpen,
  isEditModalOpen,
  setIsEditModalOpen,
}: {
  selectedProject: any | null;
  setIsAddModalOpen: (value: boolean) => void;
  isAddModalOpen: boolean;
  isEditModalOpen: boolean;
  setIsEditModalOpen: (value: boolean) => void;
}) => {
  const toast = useRef<Toast>(null);
  const { projectCreated, projectUpdated, isError, errorMessage } =
    useSelector((state: any) => state.project);
  const { leads } = useSelector((state: any) => state.lead);
  const [leadOptions, setLeadOptions] = useState<Array<{ label: string; value: string }>>([]);
  
  const [project, setProject] = useState({
    name: '',
    description: '',
    start_date: '',
    lead_id: '',
  });
  const dispatch = useDispatch<AppDispatch>();
  
  const [updatedProject, setUpdatedProject] = useState<any>({
    id: selectedProject?.id || '',
    name: selectedProject?.name || '',
    description: selectedProject?.description || '',
    start_date: selectedProject?.start_date || '',
    lead_id: selectedProject?.lead_id || '',
  });
  
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch(getAllLeads());
  }, [dispatch]);

  useEffect(() => {
    if (leads) {
      const options = leads.map((lead: any) => ({
        label: `${lead.first_name} ${lead.last_name}`,
        value: lead.id,
      }));
      setLeadOptions(options);
    }
  }, [leads]);

  const handleCreateProject = async (e: React.FormEvent) => {
    try {
      setIsLoading(true);
      e.preventDefault();

      await dispatch(createProject(project, setIsAddModalOpen));
      setProject({ name: '', description: '', start_date: '', lead_id: '' });
      setUpdatedProject({ name: '', description: '', start_date: '', lead_id: '' });
    } catch (error) {
      console.log('handleCreateProject error', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateProject = (e: React.FormEvent) => {
    try {
      setIsLoading(true);
      e.preventDefault();
      if (selectedProject) {
        dispatch(
          updateProject(
            selectedProject.id,
            {
              ...updatedProject,
              is_active: isActive,
              is_deleted: false,
            },
            () => {
              setIsEditModalOpen(false);
              const backdrop = document.querySelector('.modal-backdrop');
              if (backdrop) {
                backdrop.remove();
              }
              document.body.classList.remove('modal-open');
              document.body.style.overflow = '';
              document.body.style.paddingRight = '';
            },
          ),
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProject((prevState) => ({ ...prevState, [name]: value }));
    setUpdatedProject((prevState: any) => ({ ...prevState, [name]: value }));
  };

  const handleLeadChange = (e: any) => {
    setProject((prevState) => ({ ...prevState, lead_id: e.value }));
    setUpdatedProject((prevState: any) => ({ ...prevState, lead_id: e.value }));
  };

  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    if (projectCreated) {
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Project is created successfully',
        life: 3000,
      });
      if (accessToken) {
        dispatch(getAllProjects());
      }
    }
  }, [projectCreated]);

  useEffect(() => {
    if (selectedProject) {
      setUpdatedProject({
        id: selectedProject.id,
        name: selectedProject.name,
        description: selectedProject.description,
        start_date: selectedProject.start_date,
        lead_id: selectedProject.lead_id,
      });
    }
  }, [selectedProject]);

  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    if (projectUpdated) {
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Project is updated successfully',
        life: 3000,
      });

      if (accessToken) {
        dispatch(getAllProjects());
      }
    }
  }, [projectUpdated]);

  useEffect(() => {
    if (isError) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage || 'An error occurred',
        life: 3000,
      });
    }
  }, [isError, errorMessage]);

  return (
    <>
      <Toast ref={toast} />
      <div
        className={`modal fade ${isAddModalOpen ? 'show' : ''}`}
        id="add-project"
        style={{ display: isAddModalOpen ? 'block' : 'none' }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Add Project</h5>
              <button
                type="button"
                className="btn-close close-modal"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={() => setIsAddModalOpen(false)}
              >
                <Icon.X className="react-feather-custom"></Icon.X>
              </button>
            </div>
            <div className="modal-body pt-0">
              <form onSubmit={handleCreateProject}>
                <div className="mb-3 mt-3">
                  <label className="form-label">Lead</label>
                  <Dropdown
                    value={project.lead_id}
                    options={leadOptions}
                    onChange={handleLeadChange}
                    placeholder="Select a lead"
                    className="w-100"
                    required
                    appendTo="self"
                    panelClassName="dropdown-panel"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={project.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <input
                    type="text"
                    className="form-control"
                    name="description"
                    value={project.description}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="start_date"
                    value={project.start_date}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="text-end">
                  <button
                    type="button"
                    className="btn btn-secondary me-2"
                    onClick={() => setIsAddModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    disabled={isLoading}
                    type="submit"
                    className="btn btn-primary"
                  >
                    Add
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {/* Edit Modal */}
      <div
        className={`modal fade ${isEditModalOpen ? 'show' : ''}`}
        id="edit-project"
        style={{ display: isEditModalOpen ? 'block' : 'none' }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Project Update</h5>
              <button
                type="button"
                className="btn-close close-modal"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={() => setIsEditModalOpen(false)}
              >
                <Icon.X className="react-feather-custom"></Icon.X>
              </button>
            </div>
            <div className="modal-body pt-0">
              <form onSubmit={handleUpdateProject}>
                <div className="mb-3">
                  <label className="form-label">Lead</label>
                  <Dropdown
                    value={updatedProject.lead_id}
                    options={leadOptions}
                    onChange={handleLeadChange}
                    placeholder="Select a lead"
                    className="w-100"
                    required
                    appendTo="self"
                    panelClassName="dropdown-panel"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={updatedProject.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <input
                    type="text"
                    className="form-control"
                    name="description"
                    value={updatedProject.description}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="start_date"
                    value={updatedProject.start_date}
                    onChange={handleInputChange}
                  />
                </div>
 
                <div className="text-end">
                  <button
                    type="button"
                    className="btn btn-secondary me-2"
                    onClick={() => setIsAddModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Update
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProjectsModal; 